import {
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subWeeks,
} from "date-fns";
import type { Identifier } from "ra-core";

export const WORKBENCH_ROLES = ["sales", "purchasing", "logistics"] as const;
export const WORKBENCH_PERIODS = [
  "last-week",
  "this-week",
  "next-week",
  "this-month",
  "custom",
] as const;
export type WorkbenchFilters = {
  role: (typeof WORKBENCH_ROLES)[number];
  period: (typeof WORKBENCH_PERIODS)[number];
  account: string;
  status: string;
  from: string;
  to: string;
};
export const WORKBENCH_KEYS = [
  "loadRole",
  "loadPeriod",
  "loadAccount",
  "loadStatus",
  "loadFrom",
  "loadTo",
] as const;

export const readWorkbenchFilters = (
  params: URLSearchParams,
): WorkbenchFilters => ({
  role:
    WORKBENCH_ROLES.find((role) => role === params.get("loadRole")) ?? "sales",
  period:
    WORKBENCH_PERIODS.find((period) => period === params.get("loadPeriod")) ??
    "this-month",
  account: /^\d+$/.test(params.get("loadAccount") ?? "")
    ? (params.get("loadAccount") ?? "")
    : "",
  status: params.get("loadStatus") || "active",
  from: params.get("loadFrom") || "",
  to: params.get("loadTo") || "",
});

export const writeWorkbenchFilters = (
  params: URLSearchParams,
  filters: WorkbenchFilters,
) => {
  const next = new URLSearchParams(params);
  const values = [
    filters.role,
    filters.period,
    filters.account,
    filters.status,
    filters.from,
    filters.to,
  ];
  WORKBENCH_KEYS.forEach((key, index) => {
    if (values[index]) next.set(key, values[index]);
    else next.delete(key);
  });
  next.delete("load");
  next.delete("loadPage");
  return next;
};

export const getWorkbenchDates = (
  filters: WorkbenchFilters,
  today = new Date(),
) => {
  if (filters.period === "custom") {
    const validDate = (value: string) =>
      /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));
    return validDate(filters.from) &&
      validDate(filters.to) &&
      filters.from <= filters.to
      ? { from: filters.from, to: filters.to }
      : null;
  }
  const date =
    filters.period === "last-week"
      ? subWeeks(today, 1)
      : filters.period === "next-week"
        ? addWeeks(today, 1)
        : today;
  const monthly = filters.period === "this-month";
  return {
    from: format(
      monthly ? startOfMonth(date) : startOfWeek(date, { weekStartsOn: 1 }),
      "yyyy-MM-dd",
    ),
    to: format(
      monthly ? endOfMonth(date) : endOfWeek(date, { weekStartsOn: 1 }),
      "yyyy-MM-dd",
    ),
  };
};

export const buildWorkbenchQuery = (
  filters: WorkbenchFilters,
  ownerId: Identifier,
  statuses: string[],
) => {
  const dates = getWorkbenchDates(filters);
  const status = ["active", "holds", "all", ...statuses].includes(
    filters.status,
  )
    ? filters.status
    : "active";
  return {
    [`${filters.role}_owner_id@eq`]: ownerId,
    "pickup_date@gte": dates?.from,
    "pickup_date@lte": dates?.to,
    ...(filters.account
      ? {
          "@or": {
            "vendor_company_id@eq": filters.account,
            "customer_company_id@eq": filters.account,
          },
        }
      : {}),
    ...(status === "active"
      ? {
          "status@in": `(${statuses.filter((value) => !["completed", "closed_lost"].includes(value)).join(",")})`,
        }
      : status === "holds"
        ? { "status@in": "(pca_hold,logistics_hold,sos)" }
        : status !== "all"
          ? { "status@eq": status }
          : {}),
  };
};
